import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import ArrowLeftIcon from '../components/Icons/ArrowLeftIcon';
import EmptyState from '../components/EmptyState';
import InfoPopover from '../components/ui/InfoPopover';
import Pagination from '../components/Pagination';
import { ContentMasterProductThumb } from '../components/content-master/ContentMasterProductThumb';
import ContentMasterReviewFooter from '../components/content-master/ContentMasterReviewFooter';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { usePermissions } from '../hooks/usePermissions';
import { addNotification } from '../store/slices/notificationSlice';
import {
  isSilentForbiddenRejection,
  markContentMasterProductForEdit,
  submitContentMasterBulkApply,
  submitContentMasterBulkRetry,
  submitContentMasterProductsReject,
} from '../store/slices/contentMasterSlice';
import ContentMasterQuotaExceededModal from '../components/content-master/ContentMasterQuotaExceededModal';
import ContentMasterApplyWarningModal, {
  isApplyWarningDismissed,
} from '../components/content-master/ContentMasterApplyWarningModal';
import {
  ContentMasterEyeIcon,
  HTML_PREVIEW_CORE_FIELDS,
  useHtmlPreview,
} from '../components/content-master/HtmlPreview';
import { ContentMasterService } from '../api/generated/services/ContentMasterService';
import { ContentMasterProductView } from '../api/generated/models/ContentMasterProductView';
import type { ContentMasterLocalProductItemView } from '../api/generated/models/ContentMasterLocalProductItemView';
import type { ContentMasterBulkReviewItemView } from '../api/generated/models/ContentMasterBulkReviewItemView';
import { ContentMasterBulkReviewFieldView } from '../api/generated/models/ContentMasterBulkReviewFieldView';
import { MarketplaceListingService } from '../api/generated/services/MarketplaceListingService';
import {
  buildContentMasterChannelOptions,
  isContentMasterSourceLiteral,
} from '../utils/contentMasterWorkspace';
import { isMarketplacePlatform } from '../utils/marketplaceListing';
import {
  contentMasterStatusLabelKey,
  getContentMasterStatusClassName,
} from '../utils/contentMasterProcessingStatus';

const PAGE_LIMIT = 20;
const POLL_INTERVAL_MS = 4000;

type ListingTab = 'generating' | 'ready' | 'applying' | 'errors';

/**
 * Status filter per tab (comma-separated - `/products/local` accepts a
 * whitelist). `ready` is served by `/marketplace-listing/review` instead.
 * `generating` merges the queued + in-progress generation states; `applying`
 * and `errors` aggregate the listing_apply_* states the apply flow adds.
 */
const TAB_STATUS: Record<ListingTab, string> = {
  generating: 'listing_generate_queued,listing_generate_in_progress',
  ready: 'listing_generate_done',
  applying: 'listing_apply_queued,listing_apply_in_progress',
  errors: 'listing_generate_failed,listing_apply_failed',
};

const TABS: Array<{ key: ListingTab; labelKey: string }> = [
  { key: 'generating', labelKey: 'marketplace_listing_review_tab_in_progress' },
  { key: 'ready', labelKey: 'marketplace_listing_review_tab_done' },
  { key: 'applying', labelKey: 'marketplace_listing_review_tab_applying' },
  { key: 'errors', labelKey: 'marketplace_listing_review_tab_failed' },
];

const POLLED_TABS: ListingTab[] = ['generating', 'applying'];

/** Tabs where products are still being processed (queued / in progress). */
const PROCESSING_TABS: ListingTab[] = ['generating', 'applying'];

/**
 * Status → CSS class. Handles the listing_apply_* states (not in the generated
 * `processingStatus` enum yet); everything else defers to the shared helper.
 */
const listingStatusClass = (status?: string | null): string => {
  switch (status) {
    case 'listing_apply_queued':
      return 'content-master-status content-master-status--queued';
    case 'listing_apply_in_progress':
      return 'content-master-status content-master-status--in-progress';
    case 'listing_apply_failed':
      return 'content-master-status content-master-status--failed';
    default:
      return getContentMasterStatusClassName(
        status as ContentMasterProductView.processingStatus | null | undefined
      );
  }
};

const listingStatusLabelKey = (status?: string | null): string => {
  switch (status) {
    case 'listing_apply_queued':
      return 'content_master_processing_status_listing_apply_queued';
    case 'listing_apply_in_progress':
      return 'content_master_processing_status_listing_apply_in_progress';
    case 'listing_apply_failed':
      return 'content_master_processing_status_listing_apply_failed';
    default:
      return contentMasterStatusLabelKey(
        status as ContentMasterProductView.processingStatus | null | undefined
      );
  }
};

/**
 * Field row types the listing review shows: marketplace attributes plus the
 * core rows (396 - the per-channel `description` is a `type='core'` row).
 */
const isListingReviewField = (f: ContentMasterBulkReviewFieldView): boolean =>
  f.type === ContentMasterBulkReviewFieldView.type.MARKETPLACE ||
  f.type === ContentMasterBulkReviewFieldView.type.CORE;

/**
 * i18n keys for core rows (`label` is null by contract - keyed by `apiName`,
 * same rule as the CM bulk review). Only `description` is expected here.
 */
const CORE_FIELD_LABEL_I18N: Record<string, string> = {
  title: 'title',
  shortDescription: 'shortDescription',
  description: 'description',
};

/**
 * `(product, language)` bulk-apply pairs for one Ready-tab item: every
 * language that carries listing drafts (marketplace attributes and/or the
 * core description) becomes one pair.
 */
const pairsForItem = (
  it: ContentMasterBulkReviewItemView
): Array<{ contentMasterProductId: string; language: string }> => {
  const pairs: Array<{ contentMasterProductId: string; language: string }> = [];
  if (!it.contentMasterProductId) {
    return pairs;
  }
  for (const [lang, langView] of Object.entries(it.languages ?? {})) {
    if ((langView.fields ?? []).some(isListingReviewField)) {
      pairs.push({ contentMasterProductId: it.contentMasterProductId, language: lang });
    }
  }
  return pairs;
};

/**
 * Marketplace listing review page - status-tab board. Three tabs list product
 * cards from the Wawi-backed `GET /content-master/products` filtered by the
 * matching `listing_generate_*` status (image + title + status, no attributes):
 * Queued, Generating, Errors. The Ready tab uses `GET /marketplace-listing/review`,
 * which returns the generated attribute diff (original Wawi value vs generated
 * draft) per language. Tab badges come from `processingStatusCounts`
 * (`countsOnly`). Active tabs (queued / generating) are polled so products move
 * tabs on their own. Everything is keyed off one `source` (shop:N) - the
 * backend resolves where the products come from.
 */
const MarketplaceListingReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Subuser: reject / mark-for-edit need content_master_write, applying
  // listings to Wawi needs content_master_wawi_write. Retry stays enabled
  // (the backend decides per batch; 403 surfaces via the global interceptor).
  const { can } = usePermissions();
  const canCmWrite = can('content_master_write');
  const canCmWawiWrite = can('content_master_wawi_write');
  const [searchParams, setSearchParams] = useSearchParams();

  const { workspaceConfig, menuCounts } = useAppSelector((state) => state.contentMaster);
  const applySubmitting = useAppSelector((state) => state.contentMaster.bulkApplySubmitting);
  const bulkRetrySubmitting = useAppSelector((state) => state.contentMaster.bulkRetrySubmitting);
  const markForEditLoading = useAppSelector(
    (state) => state.contentMaster.bulkMarkForEditLoading
  );
  const dataSourceId = workspaceConfig?.dataSourceId ?? null;

  /** Only Marktplatz channels (nTyp === 6) are eligible for listing. */
  const marketplaceChannels = useMemo(() => {
    const platforms = workspaceConfig?.platforms ?? [];
    const options = buildContentMasterChannelOptions(platforms);
    const marketplaceIds = new Set(
      platforms.filter(isMarketplacePlatform).map((p) => p.id)
    );
    return options.filter((o) => marketplaceIds.has(o.value));
  }, [workspaceConfig?.platforms]);

  const sourceParam = searchParams.get('source');
  const sourceFromUrl = isContentMasterSourceLiteral(sourceParam) ? sourceParam : '';
  // Honour the URL channel (e.g. arriving from generation), otherwise stay
  // empty until the user picks a marketplace from the selector - we do NOT
  // auto-pick a channel.
  const source = useMemo(() => {
    if (sourceFromUrl && marketplaceChannels.some((o) => o.value === sourceFromUrl)) {
      return sourceFromUrl;
    }
    return '';
  }, [sourceFromUrl, marketplaceChannels]);

  const tabParam = (searchParams.get('tab') as ListingTab) || 'generating';
  const activeTab: ListingTab = TAB_STATUS[tabParam] ? tabParam : 'generating';

  const [productItems, setProductItems] = useState<ContentMasterLocalProductItemView[]>([]);
  const [reviewItems, setReviewItems] = useState<ContentMasterBulkReviewItemView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  /**
   * Single-flight guard: each loader bumps a sequence and only the newest one
   * commits its result. We intentionally do NOT abort superseded requests - the
   * poll overlapping a slow request would otherwise flood the network tab with
   * `canceled` entries - so stale responses just complete and are discarded by
   * the sequence check.
   */
  const loadSeqRef = useRef(0);

  const setUrlParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Keep the resolved source in the URL so reloads / shares stay on the same channel.
  useEffect(() => {
    if (source && sourceFromUrl !== source) {
      setUrlParam('source', source);
    }
  }, [source, sourceFromUrl, setUrlParam]);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      const seq = ++loadSeqRef.current;
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        if (activeTab === 'ready') {
          // Ready: attribute diff list (images + original vs generated). Empty
          // `source` = all channels of the data source (backend optional).
          const res = await MarketplaceListingService.getAppMarketplaceListingReview(
            source.trim() || undefined,
            undefined,
            page,
            PAGE_LIMIT,
            dataSourceId?.trim() || undefined
          );
          if (seq !== loadSeqRef.current) {
            return;
          }
          const list = res.items ?? [];
          setReviewItems(list);
          setProductItems([]);
          setTotal(res.total ?? list.length);
        } else {
          // Queued / Generating / Applying / Errors: DB-only cards from
          // `/products/local` (source-agnostic - empty `source` = all channels).
          const res = await ContentMasterService.getAppContentMasterProductsLocalByStatusList(
            TAB_STATUS[activeTab],
            source.trim() || undefined,
            dataSourceId?.trim() || undefined,
            page,
            PAGE_LIMIT
          );
          if (seq !== loadSeqRef.current) {
            return;
          }
          const list = res.items ?? [];
          setProductItems(list);
          setReviewItems([]);
          setTotal(res.total ?? list.length);
        }
      } catch {
        if (seq === loadSeqRef.current) {
          setProductItems([]);
          setReviewItems([]);
          setTotal(0);
        }
      } finally {
        if (seq === loadSeqRef.current && !opts?.silent) {
          setLoading(false);
        }
      }
    },
    [source, activeTab, dataSourceId, page]
  );

  /**
   * Keep a ref to the latest loader so the poll interval can call it without
   * being torn down and recreated whenever the loader's identity changes (which
   * was restarting the timer and skewing the cadence).
   */
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll the active tab's list (queued / generating) so finished products move
  // on their own. Created once per channel/tab; reads the latest loader from the
  // ref so it never restarts mid-cadence.
  useEffect(() => {
    if (!POLLED_TABS.includes(activeTab) || !source) {
      return;
    }
    const id = setInterval(() => {
      void loadRef.current({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activeTab, source]);

  // Tab badges read the single shared counter (`menuCounts`, polled centrally
  // in DashboardLayout). This page issues NO counter request of its own.
  const tabCounts = useMemo<Record<ListingTab, number>>(() => {
    const result = {} as Record<ListingTab, number>;
    (Object.keys(TAB_STATUS) as ListingTab[]).forEach((key) => {
      result[key] = TAB_STATUS[key]
        .split(',')
        .reduce((sum, status) => sum + (menuCounts?.[status] ?? 0), 0);
    });
    return result;
  }, [menuCounts]);

  // ---- Ready-tab apply (apply all visible, mirrors Bulk Review apply) ------
  // Every (product, language) pair that carries marketplace attribute drafts is
  // applied in one call - same body / behaviour as the Bulk Review "Apply".
  const applyPairs = useMemo(() => {
    const pairs: Array<{ contentMasterProductId: string; language: string }> = [];
    for (const it of reviewItems) {
      pairs.push(...pairsForItem(it));
    }
    return pairs;
  }, [reviewItems]);

  const performApply = useCallback(
    async (pairs: Array<{ contentMasterProductId: string; language: string }>) => {
      if (pairs.length === 0) {
        return;
      }
      try {
        const result = await dispatch(
          submitContentMasterBulkApply({ items: pairs, dataSourceId })
        ).unwrap();
        const acceptedCount = result.accepted?.length ?? 0;
        const skippedCount = result.skipped?.length ?? 0;
        if (acceptedCount === 0 && skippedCount > 0) {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('content_master_bulk_submit_no_accepted'),
            })
          );
        } else {
          const parts = [
            t('content_master_bulk_apply_success_accepted', { count: acceptedCount }),
          ];
          if (skippedCount > 0) {
            parts.push(t('content_master_bulk_submit_success_skipped', { count: skippedCount }));
          }
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: parts.join(' '),
            })
          );
        }
        void load();
      } catch (err) {
        dispatch(
          addNotification({
            type: 'error',
            title: t('error'),
            message:
              typeof err === 'string'
                ? err
                : t('error_unexpected', { defaultValue: 'Failed to apply listings' }),
          })
        );
      }
    },
    [dispatch, dataSourceId, t, load]
  );

  /**
   * Consent warning before pushing listings to the SCX schema. `null` pending
   * pairs = the footer "apply all" CTA; a per-card Apply queues that product's
   * pairs only. The "don't show again" choice is stored under the
   * listing-review key - independent from the other apply flows.
   */
  const [applyWarningOpen, setApplyWarningOpen] = useState(false);
  const [pendingApplyPairs, setPendingApplyPairs] = useState<
    Array<{ contentMasterProductId: string; language: string }> | null
  >(null);

  const handleApplyAll = useCallback(() => {
    if (applyPairs.length === 0) {
      return;
    }
    if (isApplyWarningDismissed('listing-review')) {
      void performApply(applyPairs);
      return;
    }
    setPendingApplyPairs(null);
    setApplyWarningOpen(true);
  }, [applyPairs, performApply]);

  /** Per-card Apply on the Ready tab - same warning gate, one product only. */
  const handleApplyItem = useCallback(
    (item: ContentMasterBulkReviewItemView) => {
      const pairs = pairsForItem(item);
      if (pairs.length === 0) {
        return;
      }
      if (isApplyWarningDismissed('listing-review')) {
        void performApply(pairs);
        return;
      }
      setPendingApplyPairs(pairs);
      setApplyWarningOpen(true);
    },
    [performApply]
  );

  const handleConfirmApplyWarning = useCallback(() => {
    setApplyWarningOpen(false);
    void performApply(pendingApplyPairs ?? applyPairs);
    setPendingApplyPairs(null);
  }, [pendingApplyPairs, applyPairs, performApply]);

  /** Errors-tab rows with an in-flight retry/reject (keyed by row id). */
  const [rowActionBusy, setRowActionBusy] = useState<Record<string, boolean>>({});

  const setRowBusy = useCallback((key: string, busy: boolean) => {
    setRowActionBusy((prev) => {
      const next = { ...prev };
      if (busy) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  /**
   * Errors-tab Retry - `POST /bulk/retry` is the single retry entry point for
   * all four `*_failed` statuses (incl. listing): the backend restores
   * language / source / field-group flags from the product's `bulk_config`
   * snapshot and re-dispatches the right worker message, so the client sends
   * ONLY the product id. Legacy listing products failed before the snapshot
   * existed come back as `skipped: missing_bulk_config` (warning toast) - they
   * need one regular generate submit to recover.
   */
  const handleRetryRow = useCallback(
    async (item: ContentMasterLocalProductItemView, busyKey: string) => {
      if (!item.contentMasterProductId) {
        return;
      }
      setRowBusy(busyKey, true);
      try {
        const result = await dispatch(
          submitContentMasterBulkRetry({
            items: [{ contentMasterProductId: item.contentMasterProductId }],
            dataSourceId,
          })
        ).unwrap();
        const accepted = result.accepted?.length ?? 0;
        if (accepted > 0) {
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: t('content_master_bulk_retry_success', { count: accepted }),
            })
          );
          void load();
        } else {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('content_master_bulk_retry_skipped', { count: 1 }),
            })
          );
        }
      } catch (err) {
        // Quota / subscription rejections open their shared modals instead of
        // a toast (the thunk rejects with a silent sentinel in that case).
        if (!isSilentForbiddenRejection(err)) {
          dispatch(
            addNotification({
              type: 'error',
              title: t('error'),
              message:
                typeof err === 'string'
                  ? err
                  : t('error_unexpected', { defaultValue: 'Failed to retry the product' }),
            })
          );
        }
      } finally {
        setRowBusy(busyKey, false);
      }
    },
    [dispatch, dataSourceId, t, load, setRowBusy]
  );

  /**
   * Errors-tab "Retry all failed" - server-side `all: true` mode of
   * `POST /bulk/retry`: the backend itself collects EVERY failed product of
   * the user for the data source (all pages, bulk AND listing statuses).
   * Idempotent: with nothing failed the server returns 200 with empty lists.
   */
  const handleRetryAllFailed = useCallback(async () => {
    try {
      const result = await dispatch(
        submitContentMasterBulkRetry({
          all: true,
          dataSourceId,
        })
      ).unwrap();
      const acceptedCount = result.accepted?.length ?? 0;
      const skippedCount = result.skipped?.length ?? 0;
      if (acceptedCount === 0 && skippedCount > 0) {
        dispatch(
          addNotification({
            type: 'warning',
            title: t('notification_warning'),
            message: t('content_master_bulk_retry_skipped', { count: skippedCount }),
          })
        );
        return;
      }
      if (acceptedCount > 0) {
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('content_master_bulk_retry_success', { count: acceptedCount }),
          })
        );
        void load();
      }
    } catch (err) {
      // Quota / subscription rejections open their shared modals instead of
      // a toast (the thunk rejects with a silent sentinel in that case).
      if (!isSilentForbiddenRejection(err)) {
        dispatch(
          addNotification({
            type: 'error',
            title: t('error'),
            message:
              typeof err === 'string'
                ? err
                : t('error_unexpected', { defaultValue: 'Failed to retry the products' }),
          })
        );
      }
    }
  }, [dispatch, dataSourceId, t, load]);

  /**
   * Errors-tab Reject - `POST /content-master/products/reject` moves the
   * failed product to `rejected_by_user` (no un-reject; the next generate /
   * apply submit re-queues it), so the row leaves the tab.
   */
  const handleRejectRow = useCallback(
    async (item: ContentMasterLocalProductItemView, busyKey: string) => {
      if (!item.contentMasterProductId) {
        return;
      }
      setRowBusy(busyKey, true);
      try {
        const result = await dispatch(
          submitContentMasterProductsReject({
            items: [{ contentMasterProductId: item.contentMasterProductId }],
            dataSourceId,
          })
        ).unwrap();
        if ((result.accepted?.length ?? 0) > 0) {
          dispatch(
            addNotification({
              type: 'success',
              title: t('notification_success'),
              message: t('content_master_reject_success', { count: result.accepted!.length }),
            })
          );
          void load();
        } else {
          dispatch(
            addNotification({
              type: 'warning',
              title: t('notification_warning'),
              message: t('content_master_reject_skipped', { count: 1 }),
            })
          );
        }
      } catch (err) {
        dispatch(
          addNotification({
            type: 'error',
            title: t('error'),
            message:
              typeof err === 'string'
                ? err
                : t('error_unexpected', { defaultValue: 'Failed to reject the product' }),
          })
        );
      } finally {
        setRowBusy(busyKey, false);
      }
    },
    [dispatch, dataSourceId, t, load, setRowBusy]
  );

  // Send a product back to the editor (same `mark-for-edit` endpoint as Bulk
  // Review) and drop its card from the Ready list.
  const handleMarkForEdit = useCallback(
    async (contentMasterProductId: string) => {
      try {
        await dispatch(markContentMasterProductForEdit({ contentMasterProductId })).unwrap();
        setReviewItems((prev) =>
          prev.filter((it) => it.contentMasterProductId !== contentMasterProductId)
        );
        setTotal((prev) => Math.max(0, prev - 1));
      } catch (err) {
        dispatch(
          addNotification({
            type: 'error',
            title: t('error'),
            message:
              typeof err === 'string'
                ? err
                : t('error_unexpected', { defaultValue: 'Failed to send product to editor' }),
          })
        );
      }
    },
    [dispatch, t]
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const startItem = total === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
  const endItem = Math.min(page * PAGE_LIMIT, total);

  const buildProductPath = (wawiArtikelId: number, language?: string | null): string => {
    const query = new URLSearchParams();
    query.set('source', source);
    if (language) {
      query.set('language', language);
    }
    return `/features/content-master/product/${wawiArtikelId}?${query.toString()}`;
  };

  const switchTab = (tab: ListingTab) => {
    setPage(1);
    setUrlParam('tab', tab);
  };

  // On processing tabs (generating / applying) only products already fetched
  // from Wawi (cached title + image) can be rendered; the rest are summarised
  // as a "still processing" note grouped by status.
  const isProcessingTab = PROCESSING_TABS.includes(activeTab);
  const displayableItems = useMemo(
    () =>
      isProcessingTab
        ? productItems.filter((it) => !!it.title?.trim() && !!it.mainImageUrl?.trim())
        : productItems,
    [isProcessingTab, productItems]
  );
  const pendingSummary = useMemo<Array<{ status: string; count: number }>>(() => {
    if (!isProcessingTab) {
      return [];
    }
    const counts = new Map<string, number>();
    for (const it of productItems) {
      if (it.title?.trim() && it.mainImageUrl?.trim()) {
        continue;
      }
      const status = it.processingStatus ?? '';
      counts.set(status, (counts.get(status) ?? 0) + 1);
    }
    return Array.from(counts.entries()).map(([status, count]) => ({ status, count }));
  }, [isProcessingTab, productItems]);

  const isEmpty =
    !loading &&
    (activeTab === 'ready' ? reviewItems.length === 0 : productItems.length === 0);

  return (
    <div className="gpsr-content content-master-bulk-review-page">
      <div className="gpsr-content-header-wrapper">
        <div className="gpsr-content-header-title">
          <Link to="/features/content-master" className="content-master-back-link">
            <ArrowLeftIcon />
          </Link>
          {t('marketplace_listing_review_title')}
        </div>
      </div>

      <div className="content-master-products-card">
        <div className="content-master-products-card__header">
          <div className="content-master-products-card__header-left">
            <div className="content-master-status-tabs" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={
                    activeTab === tab.key
                      ? 'content-master-status-tabs__tab content-master-status-tabs__tab--active'
                      : 'content-master-status-tabs__tab'
                  }
                  onClick={() => switchTab(tab.key)}
                >
                  <span className="content-master-status-tabs__label">{t(tab.labelKey)}</span>
                  <span className="content-master-status-tabs__count">{tabCounts[tab.key]}</span>
                </button>
              ))}
            </div>
          </div>

          {marketplaceChannels.length > 0 && (
            <div className="content-master-toolbar-selects">
              <div className="content-master-toolbar-select-wrap">
                <select
                  className="content-master-toolbar-select"
                  value={source}
                  onChange={(event) => {
                    setPage(1);
                    setUrlParam('source', event.target.value);
                  }}
                  aria-label={t('marketplace_listing_review_channel_label')}
                >
                  <option value="">{t('marketplace_listing_review_channel_placeholder')}</option>
                  {marketplaceChannels.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {isProcessingTab && pendingSummary.length > 0 && (
          <div className="marketplace-listing-processing-note" role="status">
            <span className="marketplace-listing-processing-note__label">
              {t('marketplace_listing_review_processing_note')}
            </span>
            {pendingSummary.map(({ status, count }, i) => (
              <span key={status || i} className="marketplace-listing-processing-note__item">
                <span className={listingStatusClass(status)}>
                  {t(listingStatusLabelKey(status))}
                </span>
                <span className="marketplace-listing-processing-note__count">{count}</span>
              </span>
            ))}
          </div>
        )}

        {isEmpty ? (
          <EmptyState
            title={t('marketplace_listing_review_empty')}
            description={t('marketplace_listing_review_empty_hint')}
          />
        ) : (
          <div className="content-master-table-panel">
            {loading && (
              <div className="content-master-table-panel__overlay" aria-busy="true">
                <div className="order-sidebar-spinner" aria-label={t('loading_text')} />
              </div>
            )}

            {activeTab === 'ready' ? (
              <div className="marketplace-review-cards">
                {reviewItems.map((item, idx) => {
                  const cmpId = item.contentMasterProductId ?? '';
                  return (
                    <ReviewCard
                      key={item.contentMasterProductId ?? item.wawiArtikelId ?? idx}
                      item={item}
                      productPath={
                        item.wawiArtikelId
                          ? buildProductPath(item.wawiArtikelId)
                          : null
                      }
                      onMarkForEdit={handleMarkForEdit}
                      markForEditLoading={cmpId ? markForEditLoading[cmpId] === true : false}
                      onApply={handleApplyItem}
                      applySubmitting={applySubmitting}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="content-master-table-wrapper">
                <table className="table gpsr-table content-master-table">
                  <thead>
                    <tr>
                      <th>{t('marketplace_listing_review_col_product')}</th>
                      <th>SKU</th>
                      <th>GTIN</th>
                      <th>{t('content_master_status')}</th>
                      <th className="content-master-table__edit-cell">
                        {t('marketplace_listing_review_col_actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayableItems.map((item, idx) => {
                      const rowKey = item.contentMasterProductId ?? item.wawiArtikelId ?? idx;
                      const path = item.wawiArtikelId
                        ? buildProductPath(item.wawiArtikelId, item.language)
                        : null;
                      return (
                        <tr key={rowKey} className="content-master-table__row">
                          <td>
                            <div className="content-master-table__product-cell content-master-table__cell-body">
                              <ContentMasterProductThumb imageUrl={item.mainImageUrl} alt="" />
                              {path ? (
                                <Link to={path} className="content-master-table__product-title-link">
                                  {item.title || `#${item.wawiArtikelId}`}
                                </Link>
                              ) : (
                                <span className="content-master-table__title-text">
                                  {item.title || `#${item.wawiArtikelId}`}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="content-master-table__cell-body">{item.sku || '-'}</div>
                          </td>
                          <td>
                            <div className="content-master-table__cell-body">{item.gtin || '-'}</div>
                          </td>
                          <td>
                            <div className="content-master-table__cell-body">
                              <span className={listingStatusClass(item.processingStatus)}>
                                {t(listingStatusLabelKey(item.processingStatus))}
                              </span>
                              {activeTab === 'errors' && item.bulkErrorMessage ? (
                                <span className="marketplace-listing-batch-item__error">
                                  {item.bulkErrorMessage}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="content-master-table__edit-cell">
                            <div className="content-master-table__cell-body">
                              {activeTab === 'errors' ? (
                                (() => {
                                  const busyKey = String(rowKey);
                                  const busy = rowActionBusy[busyKey] === true;
                                  return (
                                    <span className="marketplace-listing-review__row-actions">
                                      <button
                                        type="button"
                                        className="content-master-link-button"
                                        onClick={() => void handleRetryRow(item, busyKey)}
                                        disabled={busy || !item.contentMasterProductId}
                                      >
                                        {t('content_master_bulk_review_retry')}
                                      </button>
                                      <button
                                        type="button"
                                        className="content-master-link-button"
                                        onClick={() => void handleRejectRow(item, busyKey)}
                                        disabled={busy || !item.contentMasterProductId || !canCmWrite}
                                        title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
                                      >
                                        {t('content_master_bulk_review_reject')}
                                      </button>
                                    </span>
                                  );
                                })()
                              ) : path ? (
                                <Link to={path} className="content-master-table__edit-link">
                                  {t('marketplace_listing_review_open')}
                                </Link>
                              ) : (
                                '-'
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {total > 0 && (
        <Pagination
          startItem={startItem}
          endItem={endItem}
          total={total}
          totalPages={totalPages}
          page={page}
          loading={loading}
          handlePageChange={(direction) => {
            if (direction === 'prev' && page > 1) {
              setPage(page - 1);
            } else if (direction === 'next' && page < totalPages) {
              setPage(page + 1);
            }
          }}
          onGotoPage={(targetPage) => setPage(targetPage)}
        />
      )}

      <ContentMasterReviewFooter
        onRefresh={() => void load()}
        refreshing={loading}
        primaryLabel={
          activeTab === 'ready' && applyPairs.length > 0
            ? t('marketplace_listing_apply_count', { count: applyPairs.length })
            : undefined
        }
        onPrimary={activeTab === 'ready' && applyPairs.length > 0 ? handleApplyAll : undefined}
        primaryDisabled={applySubmitting || !canCmWawiWrite}
        primaryTitle={!canCmWawiWrite ? t('subuser_no_permission_tooltip') : undefined}
        secondaryLabel={
          activeTab === 'errors'
            ? t('content_master_bulk_review_footer_retry_all_failed')
            : undefined
        }
        onSecondary={activeTab === 'errors' ? handleRetryAllFailed : undefined}
        secondaryDisabled={bulkRetrySubmitting}
      />

      {/* Listing generate-retry is quota-checked (it spends AI tokens), so the
          shared quota modal must be mountable from this page too. */}
      <ContentMasterQuotaExceededModal />

      <ContentMasterApplyWarningModal
        isOpen={applyWarningOpen}
        onClose={() => {
          setApplyWarningOpen(false);
          setPendingApplyPairs(null);
        }}
        flow="listing-review"
        onConfirm={handleConfirmApplyWarning}
        isSubmitting={applySubmitting}
      />
    </div>
  );
};

/**
 * Display lines of an attribute value, via enum label(s) when options exist.
 * `multiple` values are `\n`-joined (canonical: one value per line) and render
 * as one line per value; old comma-joined drafts stay as-is (one line, no
 * conversion). Single values are always one line.
 */
const displayFieldLines = (
  field: ContentMasterBulkReviewFieldView,
  raw: string | null | undefined
): string[] => {
  if (raw == null || raw.trim() === '') {
    return [];
  }
  const options = field.allowedOptions ?? [];
  const labelFor = (value: string): string =>
    options.find((o) => o.value === value)?.label || value;
  if (field.multiple) {
    return raw
      .split('\n')
      .map((v) => labelFor(v.trim()))
      .filter(Boolean);
  }
  return [labelFor(raw)];
};

interface ReviewCardProps {
  item: ContentMasterBulkReviewItemView;
  productPath: string | null;
  onMarkForEdit: (contentMasterProductId: string) => void;
  markForEditLoading: boolean;
  /** Per-card Apply (this product only) - warning-gated by the page. */
  onApply: (item: ContentMasterBulkReviewItemView) => void;
  /** A bulk-apply submit is in flight (shared with the footer CTA). */
  applySubmitting: boolean;
}

/**
 * One Ready-tab product card: image + title + per-language marketplace-attribute
 * diff (language buttons switch the diff view). "Send to editor" pulls the
 * product out of the ready list via the shared mark-for-edit endpoint; "Apply"
 * pushes this product's drafts to the SCX schema.
 */
const ReviewCard: React.FC<ReviewCardProps> = ({
  item,
  productPath,
  onMarkForEdit,
  markForEditLoading,
  onApply,
  applySubmitting,
}) => {
  const { t } = useTranslation();
  // Subuser: mark-for-edit requires content_master_write, Apply requires
  // content_master_wawi_write.
  const { can } = usePermissions();
  const canCmWrite = can('content_master_write');
  const canCmWawiWrite = can('content_master_wawi_write');
  const cmpId = item.contentMasterProductId ?? '';
  // HTML preview modal for the core description values (raw HTML strings).
  const htmlPreview = useHtmlPreview();
  /** Languages that actually carry listing drafts (attributes or description). */
  const langKeys = useMemo(
    () =>
      Object.entries(item.languages ?? {})
        .filter(([, langView]) => (langView.fields ?? []).some(isListingReviewField))
        .map(([lang]) => lang),
    [item.languages]
  );
  const [activeLang, setActiveLang] = useState<string>(() => langKeys[0] ?? '');

  useEffect(() => {
    if (langKeys.length > 0 && !langKeys.includes(activeLang)) {
      setActiveLang(langKeys[0]);
    }
  }, [langKeys, activeLang]);

  // Two kinds of rows: the core description (396 - ordered first by the
  // backend, rendered before the attribute blocks) and the marketplace
  // attributes. wawiAttr / wawiMerkmal rows of the CM flow stay excluded.
  // Attributes split by `level` into the two UI blocks - channel
  // (Verkaufskanäle) first, then category; older snapshots without `level`
  // count as category. Within each block: required → recommended → optional
  // (stable within a group).
  const activeFields = item.languages?.[activeLang]?.fields ?? [];
  const coreFields = activeFields.filter(
    (f) => f.type === ContentMasterBulkReviewFieldView.type.CORE
  );
  const marketplaceFields = activeFields
    .filter((f) => f.type === ContentMasterBulkReviewFieldView.type.MARKETPLACE)
    .sort(
      (a, b) =>
        (a.required ? 0 : a.recommended ? 1 : 2) - (b.required ? 0 : b.recommended ? 1 : 2)
    );
  const channelFields = marketplaceFields.filter(
    (f) => f.level === ContentMasterBulkReviewFieldView.level.CHANNEL
  );
  const categoryFields = marketplaceFields.filter(
    (f) => f.level !== ContentMasterBulkReviewFieldView.level.CHANNEL
  );

  /**
   * Core row (396): the per-channel product description. `label` is null by
   * contract - the caption comes from the i18n key of `apiName`, same as the
   * CM bulk review. Values are HTML - shown as raw text with the eye-icon
   * HTML preview (mirrors the CM review core rows), no schema badge cells.
   */
  const renderCoreFieldRow = (field: ContentMasterBulkReviewFieldView, i: number) => {
    const apiName = field.apiName ?? '';
    const label = t(CORE_FIELD_LABEL_I18N[apiName] ?? apiName);
    const isHtml = HTML_PREVIEW_CORE_FIELDS.has(apiName);
    const renderValue = (value: string | null | undefined) =>
      value && value.trim() ? (
        <>
          {value}
          {isHtml ? (
            <button
              type="button"
              className="content-master-preview-button"
              onClick={() => htmlPreview.open(label, value)}
              aria-label={t('content_master_preview_html')}
              title={t('content_master_preview_html')}
            >
              <ContentMasterEyeIcon />
            </button>
          ) : null}
        </>
      ) : (
        t('marketplace_listing_attr_empty')
      );
    return (
      <tr key={apiName || `core-${i}`}>
        <td>
          <span className="marketplace-listing-attributes__name">{label}</span>
        </td>
        <td
          className={
            field.original?.trim()
              ? 'marketplace-listing-attributes__value'
              : 'marketplace-listing-attributes__value marketplace-listing-attributes__value--empty'
          }
        >
          {renderValue(field.original)}
        </td>
        <td
          className={
            field.generated?.trim()
              ? 'marketplace-listing-attributes__value marketplace-review-card__generated'
              : 'marketplace-listing-attributes__value marketplace-listing-attributes__value--empty'
          }
        >
          {renderValue(field.generated)}
        </td>
      </tr>
    );
  };

  /** Sub-header row separating the two `level` blocks inside the one table. */
  const renderGroupRow = (group: 'channel' | 'category') => (
    <tr key={`group-${group}`} className="marketplace-listing-attributes__group-row">
      <td colSpan={3}>
        {t(
          group === 'channel'
            ? 'marketplace_listing_attr_group_channel'
            : 'marketplace_listing_attr_group_category'
        )}
      </td>
    </tr>
  );

  const renderFieldRow = (field: ContentMasterBulkReviewFieldView, i: number) => {
    const originalLines = displayFieldLines(field, field.original);
    const generatedLines = displayFieldLines(field, field.generated);
    // Multi-value cells render one chip per value; single values stay plain text.
    const renderLines = (lines: string[]): React.ReactNode =>
      field.multiple ? (
        <span className="marketplace-value-chips">
          {lines.map((line, j) => (
            <span key={`${line}-${j}`} className="marketplace-value-chips__chip">
              {line}
            </span>
          ))}
        </span>
      ) : (
        lines[0]
      );
    return (
      <tr
        key={field.cAttributeId ?? field.apiName ?? i}
        className={
          field.required ? 'marketplace-listing-attributes__row--required' : undefined
        }
      >
        <td>
          <span className="marketplace-listing-attributes__name">
            {field.label || field.cAttributeId || field.apiName}
          </span>
          {field.description?.trim() ? (
            <InfoPopover
              text={field.description}
              ariaLabel={t('marketplace_listing_attr_info')}
            />
          ) : null}
          <span
            className={`marketplace-listing-attributes__badge marketplace-listing-attributes__badge--${
              field.required ? 'required' : field.recommended ? 'recommended' : 'optional'
            }`}
          >
            {field.required
              ? t('marketplace_listing_attr_required')
              : field.recommended
              ? t('marketplace_listing_attr_recommended')
              : t('marketplace_listing_attr_optional')}
          </span>
        </td>
        <td
          className={
            originalLines.length > 0
              ? 'marketplace-listing-attributes__value'
              : 'marketplace-listing-attributes__value marketplace-listing-attributes__value--empty'
          }
        >
          {originalLines.length > 0
            ? renderLines(originalLines)
            : t('marketplace_listing_attr_empty')}
        </td>
        <td
          className={
            generatedLines.length > 0
              ? 'marketplace-listing-attributes__value marketplace-review-card__generated'
              : 'marketplace-listing-attributes__value marketplace-listing-attributes__value--empty'
          }
        >
          {generatedLines.length > 0
            ? renderLines(generatedLines)
            : t('marketplace_listing_attr_empty')}
        </td>
      </tr>
    );
  };

  return (
    <div className="marketplace-review-card">
      <div className="marketplace-review-card__head">
        <ContentMasterProductThumb imageUrl={item.mainImageUrl ?? null} alt="" />
        <div className="marketplace-review-card__meta">
          {productPath ? (
            <Link to={productPath} className="marketplace-review-card__title">
              {item.titleCache || `#${item.wawiArtikelId}`}
            </Link>
          ) : (
            <span className="marketplace-review-card__title">
              {item.titleCache || `#${item.wawiArtikelId}`}
            </span>
          )}
          <div className="marketplace-review-card__sub">
            {item.sku ? <span>SKU: {item.sku}</span> : null}
            {item.gtin ? <span>GTIN: {item.gtin}</span> : null}
          </div>
        </div>
        {cmpId && (
          <>
            <button
              type="button"
              className="btn btn-primary marketplace-review-card__apply"
              onClick={() => onApply(item)}
              disabled={applySubmitting || markForEditLoading || !canCmWawiWrite}
              title={!canCmWawiWrite ? t('subuser_no_permission_tooltip') : undefined}
            >
              {t('content_master_bulk_review_apply_one')}
            </button>
            <button
              type="button"
              className="btn btn-secondary marketplace-review-card__to-edit"
              onClick={() => onMarkForEdit(cmpId)}
              disabled={markForEditLoading || !canCmWrite}
              title={!canCmWrite ? t('subuser_no_permission_tooltip') : undefined}
            >
              {markForEditLoading ? t('loading_text') : t('content_master_bulk_review_send_to_editor')}
            </button>
          </>
        )}
      </div>

      {langKeys.length > 1 && (
        <div className="marketplace-review-card__langs">
          {langKeys.map((lang) => (
            <button
              key={lang}
              type="button"
              className={
                lang === activeLang
                  ? 'marketplace-review-card__lang marketplace-review-card__lang--active'
                  : 'marketplace-review-card__lang'
              }
              onClick={() => setActiveLang(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      <table className="marketplace-listing-attributes">
        <thead>
          <tr>
            <th>{t('marketplace_listing_attr_field')}</th>
            <th>{t('marketplace_listing_review_col_original')}</th>
            <th>{t('marketplace_listing_review_col_generated')}</th>
          </tr>
        </thead>
        <tbody>
          {/* Core rows (the description) come first, then the two `level`
              blocks: channel (Verkaufskanäle), then the category attributes;
              a block is omitted when empty. */}
          {coreFields.map(renderCoreFieldRow)}
          {channelFields.length > 0 ? renderGroupRow('channel') : null}
          {channelFields.map(renderFieldRow)}
          {categoryFields.length > 0 ? renderGroupRow('category') : null}
          {categoryFields.map(renderFieldRow)}
        </tbody>
      </table>
      {htmlPreview.modal}
    </div>
  );
};

export default MarketplaceListingReviewPage;
